import {
  APILinkedWorkspacesResult,
  APIScopeData,
  APIWorkspaceAggregatedResult,
  APIWorkspaceAttributes,
  APIWorkspaceOverview,
  ArdoqId,
  CreateWorkspaceResponse,
  SetGremlinEnabledPayload,
  SetViews,
} from '@ardoq/api-types';
import {
  fetchAndRespondWith,
  fetchBlob,
  fetchEntities,
  fetchEntity,
  fetchJson,
  headers,
  Method,
  renameEntity,
  toRequestOptions,
  updateEntity,
} from './api';
import { ApiResponse } from './types';

const exportWorkspace = async (id: ArdoqId): ApiResponse<Blob> => {
  const request = new Request(
    `/api/workspace/${id}/export`,
    toRequestOptions(Method.POST, null)
  );
  return fetchBlob(request);
};

const deleteWorkspaces = async (
  workspaces: ArdoqId[]
): ApiResponse<ArdoqId[]> => {
  const request = new Request(
    `/api/workspace`,
    toRequestOptions(Method.DELETE, { workspaces })
  );
  return fetchAndRespondWith(request, workspaces);
};

type CreateWorkspaceAttributes = {
  componentTemplate: ArdoqId;
} & Pick<
  APIWorkspaceAttributes,
  'name' | 'description' | 'folder' | 'startView' | 'views'
>;

const createWorkspace = (
  workspace: CreateWorkspaceAttributes
): ApiResponse<CreateWorkspaceResponse> => {
  const request = new Request(
    `/api/workspace/create`,
    toRequestOptions(Method.POST, workspace)
  );
  return fetchJson<CreateWorkspaceResponse>(request);
};

type CreateWorkspaceWithTypesAttributes = {
  name: string;
  description: string;
  componentTypeNames: string[];
  referenceTypeNames: string[];
};

const createWorkspaceWithTypes = (
  workspace: CreateWorkspaceWithTypesAttributes
): ApiResponse<CreateWorkspaceResponse> => {
  const request = new Request(
    `/api/org-metamodel/workspace`,
    toRequestOptions(Method.POST, workspace)
  );
  return fetchJson<CreateWorkspaceResponse>(request);
};

const bulkLoadWorkspaceAggregated = (
  workspaceIds: ArdoqId[]
): ApiResponse<APIWorkspaceAggregatedResult> => {
  const request = new Request(
    `/api/workspace/subgraph`,
    toRequestOptions(Method.POST, { workspaceIds })
  );
  return fetchJson<APIWorkspaceAggregatedResult>(request);
};

type CopyWorkspaceData = {
  workspaces: [
    {
      _id: string;
      name: string;
      copyComponents: boolean;
      copyReferences: boolean;
      copyCrossWorkspaceReferences: boolean;
    },
  ];
};

type CopyReturnResponse = {
  data: {
    workspaces: { name: string; id: string }[];
  };
};

const copy = async (
  workspaceId: ArdoqId,
  newName: string,
  copyComponents: boolean,
  copyReferences: boolean,
  copyCrossWorkspaceReferences: boolean
): ApiResponse<CopyReturnResponse> => {
  const data: CopyWorkspaceData = {
    workspaces: [
      {
        _id: workspaceId,
        name: newName,
        copyComponents,
        copyReferences,
        copyCrossWorkspaceReferences,
      },
    ],
  };
  const request = new Request(
    `/api/workspace/copy/v2`,
    toRequestOptions(Method.POST, data)
  );
  return fetchJson<CopyReturnResponse>(request);
};

type copyToOrgResponse = {
  message: string;
  data: {
    'target-org': string;
    workspaces: { name: string; id: string }[];
    links: { name: string; link: string }[];
  };
};

const copyToOrg = async (
  targetOrgName: string,
  workspaces: ArdoqId[]
): ApiResponse<copyToOrgResponse> => {
  const request = new Request(
    `/api/workspace/copy`,
    toRequestOptions(Method.POST, { 'target-org': targetOrgName, workspaces })
  );
  return fetchJson<copyToOrgResponse>(request);
};

type APIWorkspaceUsage = {
  _id: ArdoqId;
  workspaceIds: ArdoqId[];
};

const usage = async (ids: ArdoqId[]) => {
  const request = new Request(`/api/workspace/usage?wids=${ids.join(',')}`, {
    headers,
  });
  return fetchJson<APIWorkspaceUsage[]>(request);
};

const overview = async (): ApiResponse<APIWorkspaceOverview[]> => {
  const request = new Request('/api/workspace/overview', { headers });
  return fetchJson<APIWorkspaceOverview[]>(request);
};

const getLinkedWorkspaces = async (
  workspaceId: ArdoqId
): ApiResponse<APILinkedWorkspacesResult> => {
  const request = new Request(
    `/api/workspace/${workspaceId}/linked-workspaces`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<APILinkedWorkspacesResult>(request);
};

const loadWorkspaceHierarchies = async (workspaceIds: ArdoqId[]) => {
  const request = new Request(
    `/api/workspace/inventory?wids=${workspaceIds.join(',')}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<APIScopeData>(request);
};

const setGremlinEnabled = async ({
  workspaceIds,
  gremlinEnabled,
}: SetGremlinEnabledPayload): ApiResponse<SetGremlinEnabledPayload> => {
  const request = new Request(
    `/api/workspace/gremlin-enabled`,
    toRequestOptions(Method.POST, {
      workspaceIds,
      gremlinEnabled,
    })
  );
  return fetchAndRespondWith(request, {
    workspaceIds,
    gremlinEnabled,
  });
};

const setViews = async ({
  workspaceId,
  views,
}: SetViews): ApiResponse<APIWorkspaceAttributes> => {
  const request = new Request(
    `/api/workspace/${workspaceId}/set-views`,
    toRequestOptions(Method.PATCH, {
      views,
    })
  );
  return fetchJson(request);
};

export const workspaceApi = {
  export: exportWorkspace,
  fetch: fetchEntity<APIWorkspaceAttributes>('workspace'),
  fetchAll: fetchEntities<APIWorkspaceAttributes>('workspace'),
  deleteWorkspaces,
  create: createWorkspace,
  createWithTypes: createWorkspaceWithTypes,
  bulkLoadWorkspaceAggregated,
  copy,
  copyToOrg,
  usage,
  overview,
  getLinkedWorkspaces,
  loadWorkspaceHierarchies,
  setGremlinEnabled,
  rename: renameEntity<APIWorkspaceAttributes>('workspace'),
  update: updateEntity<APIWorkspaceAttributes>('workspace'),
  setViews,
};
